import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Code, Zap, Shield, Database, Clock, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import APIRequestModal from '../components/APIRequestModal';

const WeatherAPI = () => {
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  React.useEffect(() => {
    document.title = 'Zeus Weather API — Hail, Storm & Severe Weather Data';
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="min-h-screen bg-white">
      {/* API Request Modal */}
      <APIRequestModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img
                src="https://customer-assets.emergentagent.com/job_roof-sales-hub/artifacts/8eyuijji_ChatGPT_Image_Jan_28__2026__08_23_42_PM-removebg-preview.png"
                alt="Zeus Weather"
                className="h-10 w-auto"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => window.open('https://portal.getzeusweather.com', '_blank')}
                className="text-slate-700 hover:text-slate-900"
              >
                Login
              </Button>
              <Link to="/">
                <Button variant="ghost" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Storm Intelligence, Delivered by API
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed mb-8">
              Zeus Weather gives developers and businesses direct access to the most comprehensive storm damage, property, and homeowner dataset in the industry. One API call returns hail and wind damage data, county assessor property details, and enriched homeowner demographics — everything you need to assess risk, validate claims, or qualify leads at scale.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                size="lg"
                onClick={() => setIsModalOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6"
              >
                Request API Access
              </Button>
              <Button
                size="lg"
                variant="ghost"
                className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6"
              >
                Read the Docs
              </Button>
            </div>
            
            <p className="text-sm text-blue-200">
              Trusted by roofing contractors, insurance carriers, and property technology platforms nationwide
            </p>
          </div>
        </div>
      </section>

      {/* Why Zeus Weather API */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Purpose-Built for Severe Weather Intelligence
              </h2>
              <p className="text-lg text-slate-600">
                Most weather APIs were designed for general forecasting — temperature, rain, and sunshine. Zeus Weather was built from the ground up to answer a different question: <strong>where did the storm hit, and how bad was the damage?</strong>
              </p>
              <p className="text-lg text-slate-600 mt-4">
                Our data engine combines NOAA storm reports, proprietary hail swath modeling, county assessor records, and insurance-industry verified datasets into a single, fast API. The result is storm intelligence that goes far beyond what generic weather providers can offer.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-2 border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Database className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Hail-Focused Data Model</h3>
                      <p className="text-slate-600">
                        Hail size, density, swath mapping, and damage probability scored by address, not just by ZIP code.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Shield className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Insurance-Grade Accuracy</h3>
                      <p className="text-slate-600">
                        Our data meets the verification standards used by carriers and adjusters for claims validation.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Clock className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Historical Depth</h3>
                      <p className="text-slate-600">
                        Access storm event records going back over a decade, with address-level resolution.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Zap className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Real-Time Alerts</h3>
                      <p className="text-slate-600">
                        Get notified the moment a storm impacts a territory you're monitoring, with severity scoring in minutes.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Globe className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Nationwide Coverage</h3>
                      <p className="text-slate-600">
                        Every county in the contiguous United States, with consistent data formatting and no coverage gaps.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Database className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Property + Homeowner Data</h3>
                      <p className="text-slate-600">
                        Storm damage enriched with county assessor records and homeowner demographics in a single call. No need to stitch together multiple vendors.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* API Endpoints & Data */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                What You Can Build With Zeus Weather API
              </h2>
            </div>
            
            <div className="space-y-6">
              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Storm Damage API</h3>
                  <p className="text-slate-700 text-lg">
                    One unified endpoint for hail and wind damage data. Query by address, coordinates, or custom polygon to retrieve hail diameter, wind gust speeds, storm timestamps, confidence scores, and damage severity ratings. All storm event types returned in a single response.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Storm Event Timeline API</h3>
                  <p className="text-slate-700 text-lg">
                    Query a complete chronological timeline of severe weather events for any address or region. Returns hail, wind, tornado, and flood events with metadata including duration, severity classification, and affected area boundaries.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Real-Time Storm Tracking & Alerts API</h3>
                  <p className="text-slate-700 text-lg">
                    Monitor active storm cells and receive webhook notifications when severe weather enters a defined geographic territory. Configurable thresholds let you filter by hail size, wind speed, or tornado warning status.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Property Risk Scoring API</h3>
                  <p className="text-slate-700 text-lg">
                    Send an address, get a storm-risk profile back. Our scoring model factors in historical storm frequency, regional hail patterns, building age, and roof type data to return a 0–100 risk score suitable for underwriting and portfolio analysis.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Property Data API</h3>
                  <p className="text-slate-700 text-lg">
                    Access county assessor records for any residential address. Returns year built, roof material, roof type, lot size, home size, and estimated home value. Sourced directly from county assessor offices nationwide and updated regularly.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Homeowner Demographics API</h3>
                  <p className="text-slate-700 text-lg">
                    Enrich any address with homeowner profile data including credit score range, income range, phone number, and Do Not Call list status. Built for lead qualification, marketing compliance, and customer segmentation.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Built for the Industries That Need Storm Data Most
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-2 border-blue-200 bg-blue-50/50">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Insurance & Claims</h3>
                  <p className="text-slate-700">
                    Carriers and adjusters use Zeus Weather API to validate hail and wind damage claims against verified storm records. Automate FNOL enrichment by attaching storm event data to incoming claims, reducing investigation time and improving accuracy.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 bg-blue-50/50">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Roofing & Restoration</h3>
                  <p className="text-slate-700">
                    Roofing contractors and restoration companies integrate Zeus data to identify storm-affected properties, prioritize canvassing routes, and build evidence-based proposals that reference confirmed storm events.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 bg-blue-50/50">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Property Technology</h3>
                  <p className="text-slate-700">
                    PropTech platforms embed Zeus storm history into property listings, inspection reports, and portfolio risk dashboards. Give buyers, sellers, and investors transparency into a property's exposure to severe weather.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 bg-blue-50/50">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Financial Services & Reinsurance</h3>
                  <p className="text-slate-700">
                    Model portfolio-level storm exposure with historical event data and forward-looking risk scores. Zeus API data feeds into catastrophe models, actuarial tools, and reinsurance placement workflows.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Developer Experience */}
      <section className="py-20 bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Integrate in Minutes, Scale to Millions
              </h2>
              <p className="text-xl text-blue-100">
                Zeus Weather API is RESTful, returns clean JSON, and requires just one API key to get started. We've designed the developer experience around three principles: fast integration, clear documentation, and zero surprises at scale.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {[
                { icon: Code, title: 'RESTful JSON API', desc: 'Standard REST conventions with predictable URL patterns' },
                { icon: Shield, title: 'API Key Authentication', desc: 'Generate your key in seconds. No OAuth flows' },
                { icon: Database, title: 'Comprehensive Docs', desc: 'Every endpoint documented with examples' },
                { icon: Zap, title: '99.9% Uptime SLA', desc: 'Multi-region infrastructure with failover' },
              ].map((feature, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
                  <feature.icon className="h-8 w-8 text-blue-300 mb-4" />
                  <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                  <p className="text-sm text-blue-100">{feature.desc}</p>
                </div>
              ))}
            </div>
            
            {/* Code Example */}
            <div className="bg-slate-950 rounded-lg p-6 overflow-x-auto">
              <div className="mb-4">
                <span className="text-green-400 text-sm font-mono">$ curl</span>
              </div>
              <pre className="text-sm font-mono text-slate-300">
{`curl -X GET "https://api.zeusweather.com/v1/property-report?address=1234+Main+St+Dallas+TX+75201" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
              </pre>
              
              <div className="mt-6 mb-4">
                <span className="text-blue-400 text-sm font-mono">Response:</span>
              </div>
              <pre className="text-sm font-mono text-slate-300">
{`{
  "address": "1234 Main St, Dallas, TX 75201",
  "storm_events": [
    {
      "date": "2025-04-12T18:43:00Z",
      "hail_diameter_inches": 1.75,
      "wind_gust_mph": 62,
      "damage_score": 78,
      "confidence": 0.94,
      "source": "NOAA + Zeus Proprietary Model"
    }
  ],
  "property": {
    "year_built": 2003,
    "roof_material": "Asphalt Shingle",
    "roof_type": "Hip",
    "lot_sqft": 8750,
    "home_sqft": 2410,
    "estimated_value": 385000
  },
  "homeowner": {
    "credit_score_range": "720-759",
    "income_range": "100k-150k",
    "phone": "972-555-0142",
    "do_not_call": false
  },
  "risk_score": 72,
  "total_events_5yr": 8
}`}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Pricing Built Around Your Use Case
              </h2>
              <p className="text-lg text-slate-600">
                Talk to our team and we'll size a plan to your request volume, data depth, and integration timeline.
              </p>
            </div>
            
            <div className="max-w-2xl mx-auto">
              <Card className="border-4 border-blue-600">
                <CardContent className="p-10">
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-slate-900 mb-2">Custom Pricing</h3>
                    <p className="text-slate-600">
                      Every integration is different. We'll build a plan around your volume, data needs, and SLA requirements.
                    </p>
                  </div>
                  <ul className="space-y-3 mb-8 max-w-md mx-auto">
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Volume-based request pricing</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Full historical archive access</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Webhooks and real-time alerts</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">99.9% uptime SLA</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Dedicated customer success manager</span>
                    </li>
                  </ul>
                  <Button
                    onClick={() => setIsModalOpen(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6"
                  >
                    Contact Sales
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Frequently Asked Questions
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  What data does the Zeus Weather API include?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  The Zeus Weather API provides hail and wind storm damage data, storm event timelines, real-time alerts, property-level risk scores, county assessor property details (year built, roof material, roof type, lot size, home size, home value), and enriched homeowner demographics (credit score range, income range, phone number, Do Not Call status).
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  How is Zeus Weather different from other weather APIs?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Most weather APIs only provide forecast data. Zeus combines storm damage intelligence with property records and homeowner demographics in a single API — no need to piece together data from separate weather, property, and data enrichment vendors.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  What homeowner and property data is available?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  For any US residential address, the API returns county assessor property details including year built, roof material, roof type, lot size, home size, and estimated value. It also returns homeowner demographic data including credit score range, income range, phone number, and Do Not Call list flag.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  What formats does the API return?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  All endpoints return JSON by default. CSV export is available for historical data queries on Professional and Enterprise plans.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  How far back does the historical data go?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Our historical storm event database covers 10+ years of hail, wind, and tornado reports for the contiguous United States, with address-level resolution.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Can I receive real-time storm alerts via the API?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Yes. Every plan supports email-based alerts and webhook delivery for real-time push notifications when storms impact your monitored territories.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  What is the API's uptime guarantee?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Zeus Weather API runs on multi-region cloud infrastructure with a 99.9% uptime SLA included on every plan.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Do you support bulk data exports?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Yes — bulk historical datasets and batch query endpoints are available for portfolio-level analysis. Contact our sales team to scope the dataset and delivery format.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Start Building With Storm Intelligence Today
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Get your API key and make your first request in under five minutes. Zeus Weather API gives you the severe weather data that generic providers can't — hail, wind, and storm damage intelligence built for the industries that need it most.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => setIsModalOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6"
              >
                Request API Access
              </Button>
              <Button
                size="lg"
                variant="ghost"
                className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6"
              >
                Read the Documentation
              </Button>
            </div>
            
            <div className="mt-8">
              <button 
                onClick={() => setIsModalOpen(true)}
                className="text-blue-200 hover:text-white underline"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_zeus-roofer-app/artifacts/7vhqji80_logo1-removebg-preview%20%281%29.png" 
                alt="Zeus Weather" 
                className="h-10 w-auto mb-4"
              />
              <p className="text-sm text-slate-400">
                Sales Operating System for Roofing Companies
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/#benefits" className="hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/#pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><a href="https://sites.leadconnectorhq.com/preview/wcmdWIrxpgWpyny0G977" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
                <li><a href="https://blog.getzeusweather.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Support</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>&copy; 2025 Zeus Weather. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WeatherAPI;
